import React, { useEffect, useState } from "react";
import "./Login.css";
import "./TermsOfUse.css";
import { useAuth } from "../../AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import LogoWithName from "../../assets/LogoName.svg";
import Loader from "../../Components/GlobalLoader/Loader";

const TermsOfUse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { acceptTerms, refreshContract } = useAuth();
  const pendingToken = location.state?.pendingToken;

  const [contractUrl, setContractUrl] = useState(location.state?.contractUrl || null);
  const [contractVersion, setContractVersion] = useState(
    location.state?.contractVersion || null
  );
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Send the doctor back to login with a short explanation. Used whenever the
  // acceptance window closes (pendingToken expired) or the contract changed.
  const backToLogin = (message) =>
    navigate("/login", { replace: true, state: { notice: message } });

  useEffect(() => {
    if (!pendingToken) {
      navigate("/login", { replace: true });
      return;
    }
    // Landed without a signed URL (or it's stale) — fetch a fresh one.
    if (!contractUrl) {
      fetchContract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContract = async () => {
    setError(null);
    setRefreshing(true);
    try {
      const data = await refreshContract(pendingToken);
      setContractUrl(data.contractUrl);
      setContractVersion(data.contractVersion);
    } catch (e) {
      if (e.response?.status === 401) {
        backToLogin("Your session timed out. Please log in again to review the terms.");
        return;
      }
      setError("Couldn't load the contract. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const onAccept = async () => {
    if (!accepted) return;
    setError(null);
    setLoading(true);
    try {
      await acceptTerms(pendingToken);
      navigate("/", { replace: true });
    } catch (e) {
      const status = e.response?.status;
      const code = e.response?.data?.code;
      if (status === 409 && code === "CONTRACT_CHANGED") {
        backToLogin("The contract was updated — please log in again to review the latest version.");
      } else if (status === 401) {
        backToLogin("Your session timed out. Please log in again to accept the terms.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!pendingToken) {
    return null;
  }

  return (
    <div className="container">
      <div className="login_container terms-container">
        <div className="left_container-section">
          <img src={LogoWithName} alt="logo" />
        </div>
        <div className="right_container-section terms-right">
          <h1 className="hello-agin">Terms of Use</h1>
          <p className="terms-subtitle">
            Please read your contract carefully and accept it to continue. This
            is a legally binding agreement governing your use of the Platform.
            {contractVersion ? ` (Version ${contractVersion})` : ""}
          </p>

          <div className="terms-pdf-box">
            {contractUrl ? (
              <iframe
                title="Contract"
                src={contractUrl}
                className="terms-pdf-frame"
              />
            ) : (
              <div className="terms-pdf-loading">
                {refreshing ? (
                  <Loader message="Loading contract" />
                ) : (
                  <p>Contract not available.</p>
                )}
              </div>
            )}
          </div>

          <div className="terms-pdf-actions">
            {contractUrl && (
              <a
                href={contractUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="terms-open-link"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              className="terms-reload-btn"
              onClick={fetchContract}
              disabled={refreshing || loading}
            >
              {refreshing ? "Reloading…" : "Reload document"}
            </button>
          </div>

          <div className="terms-accept-row">
            <input
              type="checkbox"
              id="accept-terms"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              disabled={!contractUrl}
            />
            <label htmlFor="accept-terms">
              I have read, understood, and agree to be legally bound by the Terms
              of Use, including the confidentiality and non-disclosure obligations
            </label>
          </div>

          {error && (
            <div className="error-container">
              <p className="error-message">{error}</p>
            </div>
          )}

          <div className="login_footer">
            <button
              className="login-button"
              onClick={onAccept}
              disabled={!accepted || loading || !contractUrl}
              style={{ opacity: !accepted || loading || !contractUrl ? 0.6 : 1 }}
            >
              {loading ? (
                <div className="isLoader">
                  <Loader message="Please Wait" />
                </div>
              ) : (
                "Accept & Continue"
              )}
            </button>
            <button
              className="terms-decline-btn"
              onClick={() => navigate("/login", { replace: true })}
              disabled={loading}
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
